import React from "react";

import useConfig from "./useConfig";
import usePrevious from "./usePrevious";

const GTMStateContext = React.createContext();

// Returns true if there is a valid-looking dataLayer with the specified name
const isValidDataLayer = (dataLayerName, dataLayer = window[dataLayerName]) =>
  Array.isArray(dataLayer);

// Provides a reference to the GTM `dataLayer`, based on config, and initialises GTM if necessary
const GTMProvider = ({ eventPrefix = '', children }) => {
  const { googleTagManagerId, dataLayerName } = useConfig();

  const [dataLayer, setDataLayer] = React.useState();

  // A function to dispatch events (with optional data) to the initialised dataLayer
  const dispatch = React.useCallback(
    (eventName, data = {}) => {
      if (dataLayer) {
        dataLayer.push({ event: eventPrefix + eventName, ...data });
      }
    },
    [dataLayer, eventPrefix]
  );

  React.useEffect(() => {
    if (!googleTagManagerId && !dataLayerName) {
      // GTM is not configured
    } else if (isValidDataLayer(dataLayerName)) {
      // The specified dataLayer already exists
      setDataLayer(window[dataLayerName]);
    } else if (!googleTagManagerId && dataLayerName) {
      // The specified datLayer doesn't exist, and no GTM id was given to initialise it
      console.warn(
        `useGTM() dataLayer '${dataLayerName}' not found and no 'googleTagManagerId' is specified, disabling GTM.`
      );
    } else if (googleTagManagerId) {
      // We should intialise a GTM dataLayer
      const _dataLayerName =
        dataLayerName || `dataLayer_${googleTagManagerId.replace(/\W+/g, "_")}`;

      // prettier-ignore
      /* eslint-disable */
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      '//www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script',_dataLayerName,googleTagManagerId);
      /* eslint-enable */

      setDataLayer(window[_dataLayerName]);
    }
  }, [googleTagManagerId, dataLayerName]);

  return <GTMStateContext.Provider value={dispatch}>{children}</GTMStateContext.Provider>;
};

// Imperative usage: returns the dispatch function
const useGTM = () => {
  return React.useContext(GTMStateContext);
};

// Declarative events: dispatches an event when a value changes
const useGTMChange = (value, getEventName) => {
  const dispatch = useGTM();
  const previousValue = usePrevious(value);

  return React.useEffect(() => {
    const event = getEventName(value);
    // skip if the event is null/undefined
    // or if the value itself didn't change (if the `getEventName` is dynamic/defined in render() it could change
    const shouldSkip = event == null || event === '' || previousValue === value;
    if (!shouldSkip) {
      console.log('event', event);
      dispatch(event);
    }
  }, [value, dispatch, getEventName, previousValue]);
};

export default useGTM;
export { GTMProvider, useGTMChange };
